'use client';

import { LDProvider, LDSingleKindContext } from 'launchdarkly-react-client-sdk';
import { cookies } from 'next/headers';

interface Props {
  children: React.ReactNode;
  clientSideID: string;
  session?: any;
}

/**
 * A wrapper component for the LaunchDarkly `LDProvider` that sets up the client-side
 * LaunchDarkly SDK with the provided client-side ID and user context.
 *
 * @param {Object} props - The properties for the component.
 * @param {React.ReactNode} props.children - The child components to be rendered within the `LDProvider`.
 * @param {string} props.clientSideID - The client-side ID for the LaunchDarkly environment.
 * @param {any} [props.session] - The session object containing user information. This session must
 * come from a server-side source to properly identify the user in the LaunchDarkly context.
 *
 * @returns {JSX.Element} The `LDProvider` component with the configured context and children.
 *
 * @remarks
 * If a session is provided, the user context is constructed using the session's user details.
 * The context includes properties such as `key`, `email`, `username`, `userType`, and a custom
 * property `isDraftlyUser` to indicate whether the user belongs to the Draftly domain.
 *
 * @example
 * ```tsx
 * <LDProviderWrapper clientSideID="your-client-side-id" session={serverSession}>
 *   <YourComponent />
 * </LDProviderWrapper>
 * ```
 */
export default function LDProviderWrapper({
  children,
  session,
  clientSideID,
}: Props) {
  let context: LDSingleKindContext | undefined = undefined;
  if (session) {
    context = {
      kind: 'user',
      key: session?.user?.agentId ?? session?.user?.id ?? 'anonymous',
      custom: {
        producerCode: session?.user?.producerCode ?? session?.user?.agency?.id,
        subProducerCode: session?.user?.agentId,
        releaseGroup: session?.user?.releaseGroup,
        isIndependentAgent: session?.user?.isIndependentAgent,
        guardianEmployee: getCookieValue('rbp.internal') === 'true',
      },
    };
  }
  return (
    <LDProvider clientSideID={clientSideID} context={context}>
      {children}
    </LDProvider>
  );
}

/**
 * Retrieves the value of a cookie by name.
 *
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string | undefined} The value of the cookie, or undefined if not found.
 */
export function getCookieValue(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp('(^|; )' + encodeURIComponent(name) + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[2]) : undefined;
}
